import { Info } from "lucide-react";
import { formatCurrency } from "@veorest/utils";
import { SettingsSectionHeader } from "./SettingsSectionHeader";
import type { PartnerProfile } from "../../lib/partnerSettings";
import { usePartnerSystemCounts } from "../../hooks/usePartnerSettings";
import { usePartnerWalletV2 } from "../../hooks/usePartnerWallets";
import { useI18n } from "../../i18n";

interface SystemInfoTabProps {
  profile: PartnerProfile;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--border)] py-3 last:border-b-0">
      <span className="text-sm text-[var(--text-muted)]">{label}</span>
      <span className="text-sm font-semibold text-[var(--text-primary)] break-all text-end">
        {value}
      </span>
    </div>
  );
}

export function SystemInfoTab({ profile }: SystemInfoTabProps) {
  const { t } = useI18n();
  const counts = usePartnerSystemCounts();
  const walletQuery = usePartnerWalletV2();
  const formatIqd = (value: number) => formatCurrency(value, "IQD");
  const wallet = walletQuery.data?.wallet;

  return (
    <section
      className="menu-panel"
      role="tabpanel"
      aria-label={t("settings.system.title")}
    >
      <SettingsSectionHeader
        title={t("settings.system.title")}
        hint={t("settings.system.hint")}
        icon={Info}
      />
      <div className="menu-panel__body">
        <div className="settings-block">
          <InfoRow
            label={t("settings.system.partnerId")}
            value={profile.id || t("common.dash")}
          />
          <InfoRow
            label={t("settings.system.partnerStatus")}
            value={profile.status || t("common.dash")}
          />
          <InfoRow
            label={t("settings.system.activeClients")}
            value={
              counts.isLoading
                ? t("common.loading")
                : t("settings.system.clientsValue", {
                    active: counts.activeClients,
                    total: counts.totalClients,
                  })
            }
          />
          <InfoRow
            label={t("settings.system.activeDrivers")}
            value={
              counts.isLoading
                ? t("common.loading")
                : t("settings.system.driversValue", {
                    fleet: counts.activeDrivers,
                    online: counts.onlineDrivers,
                  })
            }
          />
          <InfoRow
            label={t("settings.system.dispatchMode")}
            value={
              profile.dispatch_mode === "bypass"
                ? t("settings.system.bypassLabel")
                : t("settings.system.manualLabel")
            }
          />
          <InfoRow
            label={t("settings.system.fallback")}
            value={
              profile.oelox_fallback_enabled
                ? t("common.enabled")
                : t("common.disabled")
            }
          />
          <InfoRow
            label={t("settings.system.oeloxTripFee")}
            value={formatIqd(profile.oelox_per_trip_fee_iqd)}
          />
          <InfoRow
            label={t("settings.system.oeloxBalance")}
            value={
              walletQuery.isLoading
                ? t("common.loading")
                : wallet
                  ? formatIqd(wallet.balance)
                  : t("common.dash")
            }
          />
          <InfoRow
            label={t("settings.system.oeloxLimit")}
            value={
              walletQuery.isLoading
                ? t("common.loading")
                : wallet
                  ? formatIqd(wallet.negative_credit_limit)
                  : t("common.dash")
            }
          />
        </div>
        <p className="mt-3 text-xs text-[var(--text-muted)]">
          {t("settings.system.oeloxWalletNote")}
        </p>
      </div>
    </section>
  );
}
